import { Table, Tag, Tbody, Td, Text, Tfoot, Th, Thead, Tr } from '@chakra-ui/react';
import type { BookingRequestWithProperty } from '@kolumboos/data';
import Card from '../../../components/others/Card';
import DefaultTablePagination from '../../../components/others/DefaultTablePagination';
import TableNoResultsPlaceholder from '../../../components/tables/TableNoResultsPlaceholder';
import TableRowsLoadingPlaceholder from '../../../components/tables/TableRowsLoadingPlaceholder';
import DatesUtils, { date } from '../../../utils/dates.utils';
import BookingRequestRowAction from './BookingRequestRowAction';

interface BookingRequestsTableProps {
  bookingRequests: BookingRequestWithProperty[];
  count: number;
  handleChangePage: (page: number) => void;
  page: number;
  loading: boolean;
  rowsPerPage: number;
  setRowsPerPage: (value: number) => void;
  archive?: boolean;
}

const BookingRequestsTable: React.FC<BookingRequestsTableProps> = ({
  bookingRequests,
  count,
  handleChangePage,
  page,
  loading,
  rowsPerPage,
  setRowsPerPage,
  archive = false,
}) => {
  return (
    <Card
      sx={{ marginTop: '16px', padding: 0, position: 'relative', width: '100%', overflowX: 'auto' }}
    >
      <Table>
        <Thead>
          <Tr>
            {[
              'Proprietá',
              'Date',
              'Contatto',
              'Disponibilitá',
              archive ? 'Data di Archiviazione' : null,
              '',
            ].map((label, i) => (
              <Th key={i}>{label}</Th>
            ))}
          </Tr>
        </Thead>

        <Tbody>
          {loading ? (
            <TableRowsLoadingPlaceholder />
          ) : bookingRequests.length ? (
            bookingRequests.map((request) => (
              <BookingRequestsTableRow key={request.id} request={request} archive={archive} />
            ))
          ) : (
            <TableNoResultsPlaceholder />
          )}
        </Tbody>

        <Tfoot>
          <Tr>
            <DefaultTablePagination
              count={count}
              page={page}
              setPage={handleChangePage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          </Tr>
        </Tfoot>
      </Table>
    </Card>
  );
};

interface BookingRequestsTableRowProps {
  request: BookingRequestWithProperty;
  archive: boolean;
}

const BookingRequestsTableRow: React.FC<BookingRequestsTableRowProps> = ({ request, archive }) => {
  const propertyName = request.property.name;
  const { dateFrom, dateTo, duration, flexibleDates, closedByTenantAt } = request;
  const months = date(dateTo).diff(dateFrom, 'month');
  const plusNights = date(dateTo).subtract(months, 'month').diff(dateFrom, 'day');

  return (
    <Tr key={request.id}>
      <Td>
        <Text>{propertyName || 'Sconosciuto'}</Text>
      </Td>

      {duration! > 29 ? (
        <Td>
          <Text>
            {DatesUtils.niceDate(dateFrom)}&nbsp;{' - '}&nbsp;{DatesUtils.niceDate(dateTo)}
            {'  '}
            {`( ${months} mes${months < 2 ? 'e' : 'i'}
              ${plusNights > 0 ? ` + ${plusNights} notti` : ''} )`}
          </Text>
        </Td>
      ) : (
        <Td>
          <Text>
            {DatesUtils.niceDate(dateFrom)}&nbsp;{' - '}&nbsp;{DatesUtils.niceDate(dateTo)}
            {'  '}
            {'( ' + duration + ' notti )'}
          </Text>
        </Td>
      )}

      <Td>
        <Text>{'Sconosciuto'}</Text>
      </Td>

      <Td>
        <Tag style={{ backgroundColor: flexibleDates ? '#6DD171' : 'red', color: 'white' }}>
          {flexibleDates ? 'Date Flessibili' : 'Date Non Flessibili'}
        </Tag>
      </Td>

      {archive ? <Td>{DatesUtils.niceDate(closedByTenantAt!)}</Td> : null}

      <Td textAlign="right">
        <BookingRequestRowAction bookingRequest={request} archived={archive} />
      </Td>
    </Tr>
  );
};

export default BookingRequestsTable;
